import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { designSystem } from '@yola/ws-ui';
import { view, i18next } from '@yola/ws-sdk';
import segment from 'src/js/modules/analytics/segment';

const {
  trackers: { trackEvent },
  constants: { events, triggerIds },
} = segment;

const {
  Box,
  Dropdown,
  DropdownTrigger,
  DropdownContent,
  ActionList,
  ActionListItem,
  IconButton,
  Popover,
  PopoverTrigger,
  PopoverContent,
} = designSystem;

const HIDE_DROPDOWN_EVENT = 'ws-dropdown:hide';

const DeviceModeContainer = () => {
  const dispatch = useDispatch();
  const currentMode = useSelector(view.selectors.getDeviceMode);
  const hoveredElement = useSelector(view.selectors.getHoveredElement);
  const hideDropdown = useCallback(() => {
    window.dispatchEvent(new Event(HIDE_DROPDOWN_EVENT));
  }, []);

  const deviceModeOptions = useMemo(
    () => [
      {
        mode: view.deviceModes.DESKTOP,
        glyph: 'monitor',
        label: i18next.t('Desktop'),
        popoverTitle: i18next.t('Desktop (Base)'),
        popoverDescription: i18next.t(
          'Changes apply to all screen sizes unless you adjust Tablet or Mobile.'
        ),
      },
      {
        mode: view.deviceModes.TABLET,
        glyph: 'tablet',
        label: i18next.t('Tablet'),
        popoverTitle: i18next.t('Tablet (1199px and below)'),
        popoverDescription: i18next.t('Changes apply to Tablet and Mobile only.'),
      },
      {
        mode: view.deviceModes.MOBILE,
        glyph: 'smartphone',
        label: i18next.t('Mobile'),
        popoverTitle: i18next.t('Mobile (575px and below)'),
        popoverDescription: i18next.t('Changes apply to Mobile only.'),
      },
    ],
    []
  );

  useEffect(() => {
    hideDropdown();
  }, [hideDropdown, hoveredElement]);

  const handleSelectClick = useCallback(() => {
    trackEvent(events.EDITING_VIEW_SELECT_CLICKED, {
      breakpointType: currentMode,
      triggerId: triggerIds.YOLA_HEADER,
    });
  }, [currentMode]);

  const handleModeChange = useCallback(
    (mode) => {
      dispatch(view.actions.setDeviceMode(mode));
      trackEvent(events.EDITING_VIEW_BREAKPOINT_SWITCHED, {
        breakpointType: mode,
        triggerId: triggerIds.YOLA_HEADER,
      });
    },
    [dispatch]
  );

  return (
    <Dropdown placement="bottom-left" onShow={handleSelectClick}>
      <DropdownTrigger>
        <Box paddingRight="spacing-3-xs">
          <IconButton
            glyph={deviceModeOptions.find((option) => option.mode === currentMode).glyph}
          />
        </Box>
      </DropdownTrigger>
      <DropdownContent>
        <ActionList>
          {deviceModeOptions.map(({ mode, glyph, label, popoverTitle, popoverDescription }) => (
            <Popover
              key={mode}
              width={250}
              placement="left-center"
              appearance="dark"
              title={popoverTitle}
              description={popoverDescription}
            >
              <PopoverTrigger>
                <ActionListItem
                  title={label}
                  iconGlyph={glyph}
                  iconPlacement="left"
                  selected={currentMode === mode}
                  onClick={() => handleModeChange(mode)}
                  style={{ minWidth: '200px' }}
                />
              </PopoverTrigger>
              <PopoverContent />
            </Popover>
          ))}
        </ActionList>
      </DropdownContent>
    </Dropdown>
  );
};

export default DeviceModeContainer;
